import { useMemo, useRef } from 'react';

import resolveStatementAssignments, {
    PeriodOwner,
} from 'modules/common/components/VerifiedStatementUpload/utils/resolveStatementAssignments';
import toStatementVerdict, {
    PROCESSING_EXPIRY_MS,
    ResolvedStatementVerdict,
    StatementVerdict,
    verdictAwayFromHome,
} from 'modules/common/components/VerifiedStatementUpload/utils/statementVerdict';
import { StatementRowRef } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import { FileHoldings, fileHoldings } from 'modules/common/components/VerifiedStatementUpload/utils/removalOutcome';
import { StatementDocumentsByUuid } from 'modules/common/components/VerifiedStatementUpload/utils/heldHereFiles';
import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';
import useStatementUploadStatuses from 'services/bookkeeping/queries/useStatementUploadStatuses';

/**
 * How often to ask again while anything is still being read.
 *
 * Two tiers for the same reason the job poll had them: almost everything settles in the first
 * minute, and a member watching a file land should not wait a quarter of a minute to see it.
 * After that the odds of the next tick being the interesting one drop sharply, so the slower
 * tier costs the server much less for the same experience.
 */
const FAST_POLL_INTERVAL_MS = 4000;
const SLOW_POLL_INTERVAL_MS = 15000;
const FAST_POLL_WINDOW_MS = 60 * 1000;

/** Stable fallbacks, so a caller passing neither does not re-key the memo below every render. */
const NO_UUIDS: string[] = [];
const NO_DOCUMENTS: StatementDocumentsByUuid = new Map();

interface UseStatementUploadVerdictsArgs {
    /** Every document in one account card's window, in display order. */
    documentUuids: string[];
    /** The window's card. A document homed elsewhere is judged by its rows here — see `verdictAwayFromHome`. */
    bankAccountId: string | null;
    /** The `documentUuids` shown only because they hold a month here. Never polled or counted as in flight. */
    heldHereUuids?: string[];
    /** The member's statement documents, to name a file holding a month another one wanted. */
    documentsByUuid?: StatementDocumentsByUuid;
    /** That account's required periods, from the coverage endpoint. */
    requiredPeriods: StatementPeriod[];
    /**
     * The month the member picked, keyed by the tracker row it applies to — see `rowKey`.
     *
     * Keyed by row rather than by document so a file carrying several statements can have one of
     * its months corrected without disturbing the rest.
     *
     * Required, and callers must keep its identity stable — it is a dependency of the memo
     * below, so a fresh object each render re-derives every file's verdict, which is the
     * exact cost the uuid memos upstream exist to avoid.
     */
    assignmentByRowKey: Record<string, StatementPeriod | null | undefined>;
    /** The rows those assignments are keyed on — the same list the fold was given. */
    rows: StatementRowRef[];
    enabled?: boolean;
}

interface StatementUploadVerdicts {
    /** Verdict per document uuid. Total over `documentUuids` — every one gets an entry. */
    verdictByUuid: Record<string, ResolvedStatementVerdict>;
    /**
     * Which document owns each required month, keyed by `periodKey`. The same decision as
     * `verdictByUuid`, keyed the other way, for a caller rendering a row per month.
     *
     * Each entry names the row the ownership came from, so a correction made on a month row
     * moves that row rather than the whole document.
     */
    ownerByPeriodKey: Map<string, PeriodOwner>;
    /** Where each document holds rows, here and elsewhere — what a removal from this card leaves. */
    holdingsByUuid: Record<string, FileHoldings>;
    /** True while any document is still being read. Gates Save. */
    isAnyProcessing: boolean;
    /**
     * True once no verdict can change on its own — the signal for refreshing coverage, which
     * is the one moment the held months can have changed for the whole batch.
     */
    isAllSettled: boolean;
    isLoading: boolean;
}

/**
 * The server's verdict for each file uploaded against one account card.
 *
 * One read, not two. The frontend used to poll the automation jobs and the tracker rows
 * separately and combine them, which meant two independent clocks: the tick that saw the last
 * job reach `SUCCESS` could precede the tick that would have seen its row, and for that
 * window a correctly filed statement told the member it could not be filed. The server now
 * answers both from a single request, reading jobs before trackers so a `SUCCESS` it observes
 * implies the row was already committed.
 *
 * Read from the member-scoped `upload-statuses` endpoint rather than the onboarding progress blob
 * that used to carry it: the blob's query is gated on onboarding stage, so every surface outside
 * onboarding saw an empty verdict map and no way to tell that apart from "nothing uploaded".
 *
 * What stays here is the half the server cannot know: which file owns which month once the
 * member has staged a correction they have not saved yet. That is `resolveStatementAssignments`,
 * applied over the served verdicts.
 */
export default function useStatementUploadVerdicts({
    documentUuids,
    bankAccountId,
    heldHereUuids = NO_UUIDS,
    documentsByUuid = NO_DOCUMENTS,
    requiredPeriods,
    assignmentByRowKey,
    rows,
    enabled = true,
}: UseStatementUploadVerdictsArgs): StatementUploadVerdicts {
    /**
     * When this hook started asking, not when the files were uploaded and not when it mounted.
     *
     * The tiers are about how long *we* have been watching: a member reopening the modal on a
     * batch uploaded yesterday still wants the first few ticks to be quick, because the answer
     * they are missing is one request away, not a minute of processing away.
     *
     * Set in `refetchInterval` rather than on first render, because a render is not an ask. A
     * card mounts before its first upload and may sit there while the member goes to find
     * their statements; starting the clock then spends the fast tier — and eventually the whole
     * budget — on a hook that was not polling for anything. Fifteen quiet minutes and the
     * first real upload would never be polled for at all.
     */
    const pollingSinceRef = useRef<number | null>(null);

    const { data: statusByUuid, isLoading } = useStatementUploadStatuses({
        enabled,
        refetchOnWindowFocus: false,
        refetchInterval: (query) => {
            const statuses = query.state.data;
            // No answer for a file yet is itself a reason to keep asking — an uploaded
            // document absent from the map is one the server has not spoken about. A held-here
            // document is coverage's to answer, not this endpoint's.
            const isAnythingPending = documentUuids.some(
                (uuid) => !heldHereUuids.includes(uuid) && (statuses?.[uuid]?.status ?? 'processing') === 'processing'
            );
            if (!isAnythingPending || documentUuids.length === 0) {
                // Nothing left to watch: drop the clock so the next batch is timed from its
                // own first ask and gets the full budget, rather than inheriting one that has
                // been running since an earlier batch settled.
                pollingSinceRef.current = null;
                return false;
            }
            pollingSinceRef.current ??= Date.now();
            const elapsed = Date.now() - pollingSinceRef.current;
            // When to give up entirely: polling forever would be a modal that quietly asks the
            // server a question every fifteen seconds for as long as it is open. This is the
            // same bound the rows use to stop showing a spinner, so the files we have stopped
            // asking about are exactly the ones the list admits it has given up on.
            if (elapsed > PROCESSING_EXPIRY_MS) {
                return false;
            }
            return elapsed < FAST_POLL_WINDOW_MS ? FAST_POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS;
        },
    });

    return useMemo(() => {
        const heldHere = new Set(heldHereUuids);
        const fileNameOf = (uuid: string) => documentsByUuid.get(uuid)?.file_name;

        // What each document holds on this card, from the same rows the assignments key on.
        const rowsHereByUuid = new Map<string, StatementPeriod[]>();
        rows.forEach(({ documentUuid, period }) => {
            if (period) {
                rowsHereByUuid.set(documentUuid, [...(rowsHereByUuid.get(documentUuid) ?? []), period]);
            }
        });

        const rawVerdictByUuid: Record<string, StatementVerdict> = {};
        const holdingsByUuid: Record<string, FileHoldings> = {};
        documentUuids.forEach((uuid) => {
            const status = statusByUuid?.[uuid];
            const rowsHere = rowsHereByUuid.get(uuid) ?? [];
            holdingsByUuid[uuid] = fileHoldings({ status, bankAccountId, rowsHere });
            const home = status?.bank_account_id ?? null;
            const isHomedElsewhere = home !== null && home !== bankAccountId;
            rawVerdictByUuid[uuid] =
                isHomedElsewhere || heldHere.has(uuid)
                    ? verdictAwayFromHome(rowsHere, isHomedElsewhere ? status?.account_mask ?? '' : undefined)
                    : toStatementVerdict(status, fileNameOf);
        });

        // Ownership of a month is a fact about the whole set, and about corrections the
        // member has staged but not saved — neither of which the server can see.
        const { verdictByUuid, ownerByPeriodKey } = resolveStatementAssignments({
            documentUuids,
            verdictByUuid: rawVerdictByUuid,
            assignmentByRowKey,
            rows,
            requiredPeriods,
        });

        // Own uploads only: counting a held-here document read as a just-landed batch, refreshing
        // coverage (a Tight-plus-Plaid round trip) on every open.
        const ownUuids = documentUuids.filter((uuid) => !heldHere.has(uuid));
        const processingCount = ownUuids.filter((uuid) => rawVerdictByUuid[uuid].kind === 'processing').length;
        return {
            verdictByUuid,
            // Passed straight through. The month rows are keyed by period, and the resolver is
            // the only thing entitled to say which document owns one.
            ownerByPeriodKey,
            holdingsByUuid,
            // Counted on the raw kind, before the resolver runs, which is right for these two:
            // they decide whether to keep polling and whether coverage can be refreshed, and a
            // file the server has gone quiet on is genuinely unresolved for both purposes.
            //
            // Raw specifically, because resolution answers a different question and can mask
            // this one: a member's staged month is honoured whatever the server made of the
            // file, so a document still being read comes out of the resolver `matched`. Off the
            // resolved kind, one assignment on one in-flight file reported the whole batch
            // settled and refreshed coverage before the server had filed anything.
            //
            // Anything the member *sees* — a spinner, a count, the Save gate — must use
            // `isAwaitingVerdict` instead, which ages out a document nothing is coming back for.
            isAnyProcessing: processingCount > 0,
            isAllSettled: ownUuids.length > 0 && processingCount === 0,
            isLoading,
        };
    }, [
        documentUuids,
        heldHereUuids,
        bankAccountId,
        documentsByUuid,
        statusByUuid,
        assignmentByRowKey,
        rows,
        requiredPeriods,
        isLoading,
    ]);
}
